import { EQUIPMENT_TRANSFER_FRACS as F } from '../../components/canvas/amhs/Equipment/equipmentPortTransfer';
import type {
  AmhsLiveInventoryGate,
  AmhsTransferFrame,
  LiveGateFoupState,
  LiveGateRole,
  LiveGateTransferPhase,
} from '../../types/amhsStream';
import {
  SYNTHETIC_ENTRY_LOAD_PORT_ID,
  SYNTHETIC_EXIT_LOAD_PORT_ID,
} from '../../utils/trackNodeAnnotations';

export type GateTransferContext = {
  role: LiveGateRole;
  equipmentId: 'ENTRY-EQUIPMENT' | 'EXIT-EQUIPMENT';
  gate?: AmhsLiveInventoryGate;
  x?: number;
  y?: number;
  nodeId?: string;
};

export type GateTransferValidation = {
  ok: boolean;
  reason?: string;
};

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export function resolveGateTransferContext(
  frame: AmhsTransferFrame,
  gates: readonly AmhsLiveInventoryGate[] = []
): GateTransferContext | null {
  const gate = gates.find((candidate) => candidate.portId === frame.portId);
  const kind = String(frame.portKind ?? gate?.portKind ?? '').toUpperCase();
  const role: LiveGateRole | null =
    kind === 'ENTRY_GATE' ||
    frame.portId === SYNTHETIC_ENTRY_LOAD_PORT_ID ||
    gate?.role === 'entry'
      ? 'entry'
      : kind === 'EXIT_GATE' ||
          frame.portId === SYNTHETIC_EXIT_LOAD_PORT_ID ||
          gate?.role === 'exit'
        ? 'exit'
        : null;
  if (!role) return null;
  return {
    role,
    equipmentId:
      role === 'entry' ? 'ENTRY-EQUIPMENT' : 'EXIT-EQUIPMENT',
    gate,
    x: finite(frame.x) ? frame.x : finite(gate?.x) ? gate.x : undefined,
    y: finite(frame.y) ? frame.y : finite(gate?.y) ? gate.y : undefined,
    nodeId: frame.nodeId || gate?.nodeId || undefined,
  };
}

export function validateGateTransferStart(args: {
  context: GateTransferContext;
  frame: AmhsTransferFrame;
  ohtStopped: boolean;
  ohtAtHandoff: boolean;
  carriedFoupId: string | null;
  lockedByOhtId?: string;
}): GateTransferValidation {
  const { context, frame } = args;
  if (context.role === 'entry' && frame.eventType !== 'PICKUP') {
    return { ok: false, reason: 'ENTRY_REQUIRES_PICKUP' };
  }
  if (context.role === 'exit' && frame.eventType !== 'DROP') {
    return { ok: false, reason: 'EXIT_REQUIRES_DROP' };
  }
  if (args.lockedByOhtId && args.lockedByOhtId !== frame.ohtId) {
    return { ok: false, reason: `PORT_LOCKED_BY_${args.lockedByOhtId}` };
  }
  if (!args.ohtStopped) return { ok: false, reason: 'OHT_NOT_STOPPED' };
  if (!args.ohtAtHandoff) return { ok: false, reason: 'OHT_NOT_AT_GATE' };
  if (!finite(context.x) || !finite(context.y)) {
    return { ok: false, reason: 'HANDOFF_POINT_UNRESOLVED' };
  }
  if (context.role === 'entry' && args.carriedFoupId) {
    return { ok: false, reason: 'OHT_NOT_EMPTY' };
  }
  if (
    context.role === 'exit' &&
    args.carriedFoupId !== frame.foupId
  ) {
    return {
      ok: false,
      reason: args.carriedFoupId
        ? 'OHT_CARRIES_DIFFERENT_FOUP'
        : 'OHT_HAS_NO_FOUP',
    };
  }
  if (!frame.foupId) return { ok: false, reason: 'FOUP_ID_MISSING' };
  return { ok: true };
}

export function gateOwnershipCommitProgress(
  action: 'PICKUP' | 'DROP'
): number {
  return action === 'PICKUP' ? F.pickupOhtTake : F.dropOhtRelease;
}

export function resolveGateTransferPhase(
  progress: number,
  action: 'PICKUP' | 'DROP'
): LiveGateTransferPhase {
  const p = Math.min(1, Math.max(0, progress));
  const shutterOpenEnd =
    action === 'PICKUP' ? F.pickupShutterOpenEnd : F.dropShutterOpenEnd;
  const handoff =
    action === 'PICKUP' ? F.pickupOhtTake : F.dropOhtRelease;
  const closed =
    action === 'PICKUP' ? F.pickupShutterClosed : F.dropShutterClosed;
  if (p >= 1) return 'GATE_COMPLETE';
  if (p < F.alignEnd) return 'GATE_ALIGN';
  if (p < Math.min(shutterOpenEnd, handoff)) {
    return 'GATE_EQUIPMENT_OPEN';
  }
  if (p < Math.max(shutterOpenEnd, handoff)) {
    return 'GATE_FOUP_IN_FLIGHT';
  }
  if (p < closed) return 'GATE_OHT_HANDOFF';
  return 'GATE_EQUIPMENT_CLOSE';
}

export function resolveGateFoupState(
  progress: number,
  action: 'PICKUP' | 'DROP'
): LiveGateFoupState {
  const p = Math.min(1, Math.max(0, progress));
  if (action === 'PICKUP') {
    if (p < F.pickupShutterOpenEnd) return 'IN_ENTRY_EQUIPMENT';
    if (p < F.pickupOhtTake) return 'IN_TRANSFER';
    return 'ON_OHT';
  }
  if (p < F.dropOhtRelease) return 'ON_OHT';
  if (p < F.dropFoupInside) return 'IN_TRANSFER';
  return 'IN_EXIT_EQUIPMENT';
}

export function resolveGateShutterOpen(
  progress: number,
  action: 'PICKUP' | 'DROP'
): number {
  const p = Math.min(1, Math.max(0, progress));
  const openEnd =
    action === 'PICKUP' ? F.pickupShutterOpenEnd : F.dropShutterOpenEnd;
  const openStart =
    action === 'PICKUP' ? F.alignEnd : F.dropOhtRelease;
  const closeStart =
    action === 'PICKUP' ? F.pickupOhtTake : F.dropFoupInside;
  const closed =
    action === 'PICKUP' ? F.pickupShutterClosed : F.dropShutterClosed;
  if (p < openStart) return 0;
  if (p < openEnd) return (p - openStart) / Math.max(1e-6, openEnd - openStart);
  if (p < closeStart) return 1;
  if (p < closed) return 1 - (p - closeStart) / Math.max(1e-6, closed - closeStart);
  return 0;
}
